import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Full-screen image viewer.
 * Props:
 *   images: [{ url, label?, alt? }] | string[]
 *   startIndex: number
 *   onClose: () => void
 */
export default function Lightbox({ images = [], startIndex = 0, onClose }) {
  const list = images.map((it) => (typeof it === 'string' ? { url: it } : it));
  const [i, setI] = React.useState(startIndex);
  const total = list.length;

  const prev = useCallback(() => setI((x) => (x - 1 + total) % total), [total]);
  const next = useCallback(() => setI((x) => (x + 1) % total), [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [prev, next, onClose]);

  if (!total) return null;
  const cur = list[i] || list[0];

  return (
    <div className="fixed inset-0 bg-black/95 z-[100] flex flex-col" data-testid="lightbox" onClick={onClose}>
      <div className="flex justify-between items-center p-4 md:p-6 text-white" onClick={(e) => e.stopPropagation()}>
        <span className="overline text-white/70">
          {i + 1} / {total}{cur.label ? ` · ${cur.label}` : ''}
        </span>
        <button onClick={onClose} className="p-2 hover:opacity-80" aria-label="close" data-testid="lightbox-close">
          <X size={24} strokeWidth={1.4} />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 md:px-16 pb-6" onClick={(e) => e.stopPropagation()}>
        <img src={cur.url} alt={cur.alt || cur.label || 'image'} className="max-w-full max-h-full object-contain" data-testid="lightbox-image" />
      </div>
      {total > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 text-white p-3 hover:bg-white/10 rounded-full transition" aria-label="previous" data-testid="lightbox-prev">
            <ChevronLeft size={28} strokeWidth={1.4} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 text-white p-3 hover:bg-white/10 rounded-full transition" aria-label="next" data-testid="lightbox-next">
            <ChevronRight size={28} strokeWidth={1.4} />
          </button>
        </>
      )}
    </div>
  );
}
